import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

function readFiltersFromParams(
  searchParams: URLSearchParams,
  filterKeys: readonly string[],
  defaultFilters: Record<string, string>,
): Record<string, string> {
  const filters = { ...defaultFilters };
  for (const key of filterKeys) {
    const value = searchParams.get(key);
    if (value) filters[key] = value;
  }
  return filters;
}

/**
 * Keeps Pro list search + filter chips in the URL so Back from a detail
 * screen restores the list state. Transient — uses replace navigation only.
 */
export function useProListUrlState(
  filterKeys: readonly string[],
  defaultFilters: Record<string, string>,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [activeFilters, setActiveFilters] = useState(() =>
    readFiltersFromParams(searchParams, filterKeys, defaultFilters),
  );

  useEffect(() => {
    const managed = new Set<string>([...filterKeys, 'search']);
    const next = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (!managed.has(key)) next.set(key, value);
    });

    if (search) next.set('search', search);
    for (const key of filterKeys) {
      const value = activeFilters[key] ?? '';
      if (value) next.set(key, value);
    }

    const nextStr = next.toString();
    const curStr = searchParams.toString();
    if (nextStr !== curStr) {
      setSearchParams(next, { replace: true });
    }
  }, [search, activeFilters, filterKeys, searchParams, setSearchParams]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleClearFilters = useCallback(
    (alsoRemove: string[] = []) => {
      setSearch('');
      setActiveFilters({ ...defaultFilters });

      const managed = new Set<string>([...filterKeys, 'search', ...alsoRemove]);
      const next = new URLSearchParams();
      searchParams.forEach((value, key) => {
        if (!managed.has(key)) next.set(key, value);
      });
      setSearchParams(next, { replace: true });
    },
    [defaultFilters, filterKeys, searchParams, setSearchParams],
  );

  return {
    search,
    setSearch,
    activeFilters,
    handleFilterChange,
    handleClearFilters,
  };
}
